import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { Zap, Send, UserPlus, TrendingUp, ArrowLeft, Instagram, MessageCircle, Users, BarChart2, MousePointerClick, RotateCw } from "lucide-react";
import { callNonStoreApi } from "../NonStoreApis";
import { InstagramIntegrationApi } from "../Api/ApiEndPoints";
import { enqueueSnackbar } from "notistack";

import PageHeader from "../components/ui/PageHeader";
import PageContainer from "../components/ui/PageContainer";
import StatCard from "../components/ui/StatCard";
import Button from "../components/ui/Button";

import { callNonStoreApiNew } from "../NonStoreApis";
import { FetchDashboardOverview } from "../Api/ApiEndPoints";

import InstaCommentToDmAutomation from "../Integrations/Instagram/InstaCommentToDmAutomation";
import WebAutomationCreation from "../Integrations/Instagram/WebAutomationCreation";

function HomeDashboard() {
  const apiUserData = useSelector((state) => state.user.apiUserData);
  const { credentials } = useSelector((state) => state.instagram || {});

  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  // null = main view; { media, automation, isEdit } = creation/edit form
  const [creationProps, setCreationProps] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const handleConnectInstagram = () => {
    setConnecting(true);
    callNonStoreApi(InstagramIntegrationApi, null, {
      params: { username: apiUserData?.userName },
    })
      .then((res) => { window.location.href = res.data.instagramAuthUrl; })
      .catch(() => {
        enqueueSnackbar("Error connecting to Instagram. Please try again.", { variant: "error" });
        setConnecting(false);
      });
  };

  const firstName = useMemo(() => {
    const name = apiUserData?.Name || apiUserData?.name || "";
    return name ? name.split(" ")[0] : "there";
  }, [apiUserData]);

  useEffect(() => {
    let cancelled = false;
    if (!credentials?.user_id) { setOverview(null); setOverviewLoading(false); return; }
    setOverviewLoading(true);
    callNonStoreApiNew(
      "get",
      `${FetchDashboardOverview}?instaUserId=${encodeURIComponent(credentials.user_id)}&days=7`
    )
      .then((res) => {
        if (cancelled) return;
        if (res.status === 200) setOverview(res.data || null);
      })
      .catch((err) => { console.error("dashboard overview API error:", err?.response?.status, err?.response?.data || err?.message); })
      .finally(() => { if (!cancelled) setOverviewLoading(false); });
    return () => { cancelled = true; };
  }, [credentials]);

  const formatConversion = (v) =>
    v == null ? "—" : `${Math.round(v * 100)}%`;

  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    window.dispatchEvent(new CustomEvent("refresh-automations"));
    setTimeout(() => setRefreshing(false), 1000);
  };

  const openCreation = (media, automation, isEdit) => {
    window.history.pushState({ creationOpen: true }, "");
    setCreationProps({ media, automation, isEdit });
  };

  const closeCreation = () => setCreationProps(null);

  // Browser back closes the inline form instead of navigating away
  useEffect(() => {
    const onPopState = () => setCreationProps(null);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // ─── Creation / edit form ─────────────────────────────────────────────────
  if (creationProps) {
    return (
      <>
        <PageHeader
          title={`Hey ${firstName} 👋`}
          subtitle="Here's what's happening with your Instagram automations."
        />
        <PageContainer>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft size={16} />}
              onClick={closeCreation}
            >
              Back
            </Button>
          </Box>
          <WebAutomationCreation
            media={creationProps.media}
            automation={creationProps.automation}
            isEdit={creationProps.isEdit}
            onClose={closeCreation}
            onSave={closeCreation}
            instaUserId={credentials?.user_id}
            instaId={credentials?.id}
            credentials={credentials}
          />
        </PageContainer>
      </>
    );
  }

  // ─── No Instagram connected ───────────────────────────────────────────────
  const FEATURES = [
    {
      icon: MessageCircle,
      color: "#f97316",
      bg: "#fff7ed",
      title: "Comment Automation",
      desc: "Automatically send personalized DMs to anyone who comments on your posts — no manual follow-up needed.",
    },
    {
      icon: MousePointerClick,
      color: "#8b5cf6",
      bg: "#f5f3ff",
      title: "One-click Lead Capture",
      desc: "Every comment becomes a lead. Collect names, contact info, and intent — all on autopilot.",
    },
    {
      icon: Users,
      color: "#0ea5e9",
      bg: "#f0f9ff",
      title: "Audience Growth",
      desc: "Engage every commenter instantly to build stronger relationships and grow a loyal audience.",
    },
    {
      icon: BarChart2,
      color: "#10b981",
      bg: "#f0fdf4",
      title: "Analytics & Insights",
      desc: "Track DMs sent, conversion rates, and campaign performance — all from one dashboard.",
    },
  ];

  if (!credentials?.user_id) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "#f8fafc",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: "24px",
          py: "48px",
        }}
      >
        {/* Hero card */}
        <Box
          sx={{
            background: "#ffffff",
            borderRadius: "20px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 4px 24px 0 rgba(15,23,42,0.06)",
            p: { xs: "32px 24px", md: "48px 56px" },
            maxWidth: 760,
            width: "100%",
            textAlign: "center",
          }}
        >
          {/* Instagram gradient icon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "22px",
              background: "linear-gradient(135deg, #f97316 0%, #ec4899 100%)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 24px rgba(249,115,22,0.30)",
              mb: "24px",
            }}
          >
            <Instagram size={40} color="#ffffff" strokeWidth={1.75} />
          </Box>

          {/* Headline */}
          <Box sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, color: "#0f172a", lineHeight: 1.25, mb: "12px" }}>
            Turn Instagram comments into customers
          </Box>
          <Box sx={{ fontSize: 15, color: "#64748b", maxWidth: 460, mx: "auto", lineHeight: 1.6, mb: "32px" }}>
            Connect your Instagram Business account to automate DMs, capture leads, and grow your audience — all from one place.
          </Box>

          {/* CTA button */}
          <Box
            role="button"
            tabIndex={0}
            onClick={handleConnectInstagram}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleConnectInstagram(); }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              px: "28px",
              py: "13px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #f97316 0%, #ec4899 100%)",
              color: "#ffffff",
              fontSize: 15,
              fontWeight: 700,
              cursor: connecting ? "not-allowed" : "pointer",
              opacity: connecting ? 0.75 : 1,
              boxShadow: "0 4px 16px rgba(249,115,22,0.35)",
              transition: "opacity 150ms ease-out, box-shadow 150ms ease-out",
              "&:hover": { opacity: connecting ? 0.75 : 0.92, boxShadow: "0 6px 20px rgba(249,115,22,0.40)" },
              mb: "12px",
            }}
          >
            {connecting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <Instagram size={18} />}
            {connecting ? "Redirecting to Instagram…" : "Connect Instagram"}
          </Box>

          <Box sx={{ fontSize: 12, color: "#94a3b8", mt: "4px" }}>
            Requires an Instagram Business or Creator account
          </Box>
        </Box>

        {/* Feature cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: "16px",
            maxWidth: 760,
            width: "100%",
            mt: "24px",
          }}
        >
          {FEATURES.map(({ icon: Icon, color, bg, title, desc }) => (
            <Box
              key={title}
              sx={{
                background: "#ffffff",
                borderRadius: "14px",
                border: "1px solid #e2e8f0",
                p: "20px 22px",
                display: "flex",
                gap: "16px",
                alignItems: "flex-start",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  background: bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon size={20} color={color} strokeWidth={2} />
              </Box>
              <Box>
                <Box sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a", mb: "4px" }}>{title}</Box>
                <Box sx={{ fontSize: 13, color: "#64748b", lineHeight: 1.55 }}>{desc}</Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // ─── Main view ────────────────────────────────────────────────────────────
  return (
    <>
      <PageHeader
        title={`Hey ${firstName} 👋`}
        subtitle="Here's what's happening with your Instagram automations."
        actions={
          <Box
            role="button"
            tabIndex={0}
            title="Refresh"
            onClick={handleRefresh}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleRefresh(); }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              px: "12px",
              height: 36,
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              backgroundColor: "#ffffff",
              cursor: refreshing ? "not-allowed" : "pointer",
              fontSize: 13,
              fontWeight: 500,
              color: "#475569",
              transition: "background-color 150ms ease-out, border-color 150ms ease-out",
              mr: "24px",
              "&:hover": { backgroundColor: "#f8fafc", borderColor: "#cbd5e1" },
              "&:focus-visible": { outline: "2px solid #f97316", outlineOffset: 2 },
            }}
          >
            <RotateCw
              size={15}
              strokeWidth={2.25}
              color="#64748b"
              style={{
                transition: "transform 600ms ease-out",
                transform: refreshing ? "rotate(360deg)" : "rotate(0deg)",
              }}
            />
            Refresh
          </Box>
        }
      />
      <PageContainer>
        {/* Stat cards */}
        <Box sx={{ mb: "24px" }}>
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} sm={6} lg={3} sx={{ display: "flex" }}>
              <StatCard
                icon={Zap}
                label="Active Automations"
                value={overview?.activeAutomations ?? 0}
                loading={overviewLoading}
                accent="orange"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3} sx={{ display: "flex" }}>
              <StatCard
                icon={Send}
                label="DMs Sent (Month)"
                value={overview?.dmsSent ?? "—"}
                delta={overview?.dmsSentDelta ? { ...overview.dmsSentDelta, period: "vs. prior 7d" } : null}
                loading={overviewLoading}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Instagram feed grid — click any post to create/edit automation */}
        <InstaCommentToDmAutomation
          onPostSelected={openCreation}
        />
      </PageContainer>
    </>
  );
}

export default HomeDashboard;
